import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  register,
  login,
  loginWithEmail,
  loginWithGoogle,
  loginWithFace,
} from "../../api/auth";
import { getCurrentUser, updateProfile } from "../../api/users";
import { User, RegisterData, LoginData, AuthProvider, UpdateProfileData } from "../../types";
import {
  saveAuthSession,
  getAuthSession,
  clearAuthData,
} from "../../utils/storage.utils";

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  initialCheckDone: boolean;
  error: string | null;
  registrationSuccess: boolean; // NEW: track registration success
  lastAuthProvider: AuthProvider | null; // NEW: track which method was used
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: false,
  initialCheckDone: false,
  error: null,
  registrationSuccess: false,
  lastAuthProvider: null,
};

// const hasStoredToken = (): boolean => {
//   const session = getAuthSession();
//   return !!session?.token;
// };

// Register thunk - does NOT auto-login, just creates account
export const registerUser = createAsyncThunk(
  "auth/register",
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      const response = await register(data);
      // Return the auth provider used for registration
      return {
        success: true,
        authProvider: data.auth_provider,
        email: response.email,
        firstName: response.first_name,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Registration failed"
      );
    }
  }
);

// Login thunk - saves session and authenticates
export const loginUser = createAsyncThunk(
  "auth/login",
  async (data: LoginData, { rejectWithValue }) => {
    try {
      const response = await login(data);
      const user: User = {
        id: response.user_id,
        email: response.email,
        first_name: response.first_name,
        last_name: response.last_name,
        full_name: response.full_name,
        avatar_url: null,
        role: response.role,
        auth_provider: response.auth_provider,
        is_active: true,
        email_verified: false,
        created_at: new Date().toISOString(),
      };
      saveAuthSession(response.access_token, user);
      return { token: response.access_token, user };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || "Login failed");
    }
  }
);

// Email login thunk
export const loginWithEmailThunk = createAsyncThunk(
  "auth/loginWithEmail",
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await loginWithEmail(email, password);
      const user: User = {
        id: response.user_id,
        email: response.email,
        first_name: response.first_name,
        last_name: response.last_name,
        full_name: response.full_name,
        avatar_url: null,
        role: response.role,
        auth_provider: response.auth_provider,
        is_active: true,
        email_verified: false,
        created_at: new Date().toISOString(),
      };
      saveAuthSession(response.access_token, user);
      return { token: response.access_token, user };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || "Login failed");
    }
  }
);

// Google login thunk
export const loginWithGoogleThunk = createAsyncThunk(
  "auth/loginWithGoogle",
  async ({ googleToken }: { googleToken: string }, { rejectWithValue }) => {
    try {
      const response = await loginWithGoogle(googleToken);
      const user: User = {
        id: response.user_id,
        email: response.email,
        first_name: response.first_name,
        last_name: response.last_name,
        full_name: response.full_name,
        avatar_url: null,
        role: response.role,
        auth_provider: response.auth_provider,
        is_active: true,
        email_verified: true,
        created_at: new Date().toISOString(),
      };
      saveAuthSession(response.access_token, user);
      return { token: response.access_token, user };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Google login failed"
      );
    }
  }
);

// Face login thunk
export const loginWithFaceThunk = createAsyncThunk(
  "auth/loginWithFace",
  async ({ faceImage }: { faceImage: string }, { rejectWithValue }) => {
    try {
      const response = await loginWithFace(faceImage);
      const user: User = {
        id: response.user_id,
        email: response.email,
        first_name: response.first_name,
        last_name: response.last_name,
        full_name: response.full_name,
        avatar_url: null,
        role: response.role,
        auth_provider: response.auth_provider,
        is_active: true,
        email_verified: false,
        created_at: new Date().toISOString(),
      };
      saveAuthSession(response.access_token, user);
      return { token: response.access_token, user };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Face login failed"
      );
    }
  }
);

// Fetch current user
export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const user = await getCurrentUser();
      return user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to fetch user"
      );
    }
  }
);

// Check auth on app load
export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const session = getAuthSession();
      if (!session || !session.token) {
        return rejectWithValue("No session found");
      }
      const user = await getCurrentUser();
      return { token: session.token, user, isNewLogin: false };
    } catch (error: any) {
      clearAuthData();
      return rejectWithValue("Session expired");
    }
  }
);

// Update user profile (first_name, last_name)
export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (data: UpdateProfileData, { rejectWithValue }) => {
    try {
      const user = await updateProfile(data);
      return user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to update profile"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      clearAuthData();
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;
      state.registrationSuccess = false;
      state.lastAuthProvider = null;
      state.initialCheckDone = true;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearRegistrationSuccess: (state) => {
      state.registrationSuccess = false;
      state.lastAuthProvider = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    // Register - does NOT authenticate, just marks success
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.registrationSuccess = false;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.registrationSuccess = true;
        state.lastAuthProvider = action.payload.authProvider;
        // NOTE: We do NOT set isAuthenticated or save token here
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.registrationSuccess = false;
      });

    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.registrationSuccess = false;
        state.initialCheckDone = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Email login
    builder
      .addCase(loginWithEmailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithEmailThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.initialCheckDone = true;
      })
      .addCase(loginWithEmailThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Google login
    builder
      .addCase(loginWithGoogleThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogleThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.initialCheckDone = true;
      })
      .addCase(loginWithGoogleThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Face login
    builder
      .addCase(loginWithFaceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithFaceThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.initialCheckDone = true;
      })
      .addCase(loginWithFaceThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch current user
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Check auth
    builder
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.initialCheckDone = true;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.initialCheckDone = true;
      });

    // Update user profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError, clearRegistrationSuccess, updateUser } =
  authSlice.actions;
export default authSlice.reducer;
